import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosConfig from "../../services/Axios";
import { unsetUser, setUser } from "../auth/authSlice";

export const fetchOrder = createAsyncThunk(
  "fetchOrder",
  async (data, thunkAPI) => {
    try {
      return await axiosConfig
        .get(process.env.REACT_APP_API_URL + data)
        .then(async (response) => {
          return response.data;
        });
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const createOrder = createAsyncThunk(
  "createOrder",
  async (data, thunkAPI) => {
    try {
      return await axiosConfig
        .post(process.env.REACT_APP_API_URL + "/api/purchase_orders", data)
        .then(async (response) => {
          return response.data;
        });
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const addItemToBasket = createAsyncThunk(
  "addItemToBasket",
  async (data, thunkAPI) => {
    if (
      !thunkAPI.getState().auth.user.order ||
      thunkAPI.getState().auth.user.order === "null"
    ) {
      thunkAPI.dispatch(
        createOrder({
          partner: thunkAPI.getState().auth.user.partner,
          purchaseOrderItems: [
            {
              item: data,
            },
          ],
        })
      );
    } else {
      try {
        return await axiosConfig
          .post(process.env.REACT_APP_API_URL + "/api/purchase_order_items", {
            item: data,
            purchaseOrder: thunkAPI.getState().auth.user.order,
          })
          .then(async (response) => {
            return response.data;
          });
      } catch (e) {
        return thunkAPI.rejectWithValue(e);
      }
    }
  }
);

export const removeItemFromBasket = createAsyncThunk(
  "removeItemFromBasket",
  async (data, thunkAPI) => {
    try {
      await axiosConfig.delete(process.env.REACT_APP_API_URL + data["@id"]);
      let filteredEntities = thunkAPI
        .getState()
        .basket.entities.filter((i) => i["@id"] !== data["@id"]);
      return filteredEntities;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateQuantity = createAsyncThunk(
  "updateQuantity",
  async ({ quantity, purchaseOrderItem }, thunkAPI) => {
    try {
      return await axiosConfig
        .patch(
          process.env.REACT_APP_API_URL + purchaseOrderItem["@id"],
          { quantity },
          {
            headers: { "Content-Type": "application/merge-patch+json" },
          }
        )
        .then(async (response) => {
          return response.data;
        });
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const updateBasket = createAsyncThunk(
  "updateBasket",
  async (data, thunkAPI) => {
    try {
      return await axiosConfig
        .patch(process.env.REACT_APP_API_URL + data.basket, data.data, {
          headers: { "Content-Type": "application/merge-patch+json" },
        })
        .then(async (response) => {
          return {
            ...response.data,
            order: thunkAPI.getState().auth.user.order,
          };
        });
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const removeBasket = createAsyncThunk(
  "removeBasket",
  async ({ basket }, thunkAPI) => {
    try {
      return await axiosConfig
        .delete(process.env.REACT_APP_API_URL + basket)
        .then(async (response) => {
          return basket;
        });
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const basketSlice = createSlice({
  name: "basket",
  initialState: {
    entities: {},
    loading: false,
    state: null,
  },
  reducers: {
    fetchOrder,
  },
  extraReducers: (builder) => {
    builder
      .addCase(unsetUser, (state) => {
        state.entities = {};
        state.state = {};
        window.localStorage.removeItem("order");
      })
      .addCase(setUser, (state) => {
        state.entities = {};
        state.state = {};
        window.localStorage.removeItem("order");
      })
      .addCase(fetchOrder.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload) {
          state.entities = response?.payload.purchaseOrderItems;
          state.state = response?.payload.state;
        }
      })
      .addCase(createOrder.pending, (state) => {
        state.entities = {};
        state.loading = true;
      })
      .addCase(createOrder.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload) {
          state.entities = response?.payload.purchaseOrderItems;
          state.state = response?.payload.state;
        }
      })
      .addCase(createOrder.rejected, (state) => {
        state.entities = {};
        state.loading = false;
      })
      .addCase(addItemToBasket.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload) {
          state.entities.push(response.payload);
        }
      })
      .addCase(removeItemFromBasket.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload) {
          state.entities = response.payload;
        }
      })
      .addCase(updateQuantity.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateQuantity.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateQuantity.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload && Array.isArray(state.entities)) {
          let itemIndex = state.entities.findIndex(
            (i) => i.id === response.payload.id
          );
          state.entities[itemIndex] = response?.payload;
        }
      })
      .addCase(updateBasket.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBasket.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateBasket.fulfilled, (state, response) => {
        state.loading = false;
        if (response?.payload && response?.payload.order) {
          state.state = response?.payload.state;
          if (
            response?.payload.state !== "in_progress" &&
            response.payload["@id"] === response.payload.order
          ) {
            state.entities = {};
            state.state = null;
          }
        }
      })
      .addCase(removeBasket.fulfilled, (state, response) => {
        state.loading = false;
        state.state = null;
        state.entities = {};
      });
  },
});

export default basketSlice.reducer;
